import { useEffect, useMemo } from 'react'

import { match } from 'ts-pattern'

import type {
  Condition,
  ConditionalOperator
} from 'Automations/nodeDefinitions/conditions'
import type { ProductPricingModel } from 'Automations/nodeDefinitions/conditions/helpers/functionStrings'
import type { BillingType, SelectableBillingFrequency } from '@sequencehq/utils'
import {
  billingFrequencyRowAvailable,
  defaultBillingFrequencySubOperator
} from './priceAttributeExhaustion'

export const PRODUCT_OPERATOR_KEYS = [
  'DISCOUNT',
  'HAS_DISCOUNT',
  'PRICING_MODEL',
  'BILLING_FREQUENCY',
  'BILLING_TYPE'
] as const

export type ProductOperator = (typeof PRODUCT_OPERATOR_KEYS)[number]

export const DISCOUNT_SUB_OPERATORS = [
  'LESS_THAN',
  'MORE_THAN',
  'EQUALS'
] as const
export type DiscountSubOperator = (typeof DISCOUNT_SUB_OPERATORS)[number]

export const isDiscountSubOperator = (op: unknown): op is DiscountSubOperator =>
  DISCOUNT_SUB_OPERATORS.includes(op as DiscountSubOperator)

/**
 * Sub-operator shared by the operators that compare a price attribute — IS
 * asserts "every price matches", IS_NOT asserts "at least one doesn't".
 */
export type IsSubOperator = 'IS' | 'IS_NOT'

export const readIsSubOperator = (
  operator: ProductConditionValue['operator']
): IsSubOperator | undefined =>
  operator === 'IS' || operator === 'IS_NOT' ? operator : undefined

/**
 * The value fields edited by an is / is not comparison against one attribute
 * of a product's prices.
 */
export type PriceAttributeField =
  | 'pricingModel'
  | 'billingFrequency'
  | 'billingType'

export type ProductConditionValue = {
  productId?: string
  /**
   * Shared sub-operator field. For DISCOUNT this is one of LESS_THAN /
   * MORE_THAN / EQUALS; for PRICING_MODEL and BILLING_FREQUENCY it's IS /
   * IS_NOT. Reused across primary operators so the value shape stays flat;
   * consumers discriminate via the parent operator.
   */
  operator?: DiscountSubOperator | IsSubOperator
  amount?: string
  unit?: string
  /** PRODUCT.HAS_DISCOUNT only: the picked yes/no answer. */
  hasDiscount?: boolean
  /** PRODUCT.PRICING_MODEL only: the chosen pricing model. */
  pricingModel?: ProductPricingModel
  /** PRODUCT.BILLING_FREQUENCY only: the chosen billing frequency. */
  billingFrequency?: SelectableBillingFrequency
  /** PRODUCT.BILLING_TYPE only: the chosen billing type. */
  billingType?: BillingType
}

export type ProductFieldKey =
  | 'productId'
  | 'operator'
  | 'amount'
  | 'hasDiscount'
  | 'pricingModel'
  | 'billingFrequency'
  | 'billingType'

export const readValue = (value: unknown): ProductConditionValue =>
  typeof value === 'object' && value !== null
    ? (value as ProductConditionValue)
    : {}

export const appendTestId = (
  base: string | undefined,
  suffix: string
): string | undefined => (base ? `${base}.${suffix}` : undefined)

export type SameProductSibling = Omit<ProductConditionValue, 'operator'> & {
  /**
   * The picked sub-operator on the sibling row. For DISCOUNT this is
   * less-than / more-than / equals; for PRICING_MODEL and BILLING_FREQUENCY
   * it's IS / IS_NOT. `undefined` on rows whose primary operator has no
   * sub-operator (e.g. HAS_DISCOUNT).
   */
  subOperator: DiscountSubOperator | IsSubOperator | undefined
  /** Primary operator (DISCOUNT, HAS_DISCOUNT, PRICING_MODEL, or
   *  BILLING_FREQUENCY). */
  operator: string
}

const useSameProductSiblings = (
  productId: string | undefined,
  siblingConditions: Array<Condition>
): SameProductSibling[] =>
  useMemo(() => {
    if (!productId) return []
    return siblingConditions
      .map(c => {
        const { operator: subOperator, ...rest } = readValue(c.value)
        return { ...rest, subOperator, operator: c.operator }
      })
      .filter(s => s.productId === productId)
  }, [productId, siblingConditions])

/**
 * True if a sibling row on the same product is a fully-committed DISCOUNT
 * comparator (primary operator is DISCOUNT and its sub-operator is selected).
 * Used by HAS_DISCOUNT to constrain its yes/no answer — once the user has
 * said "the discount is < $100" on this product, the only consistent
 * answer to "has a discount?" is "yes".
 */
export const hasCommittedDiscountSibling = (
  sameProductSiblings: SameProductSibling[]
): boolean =>
  sameProductSiblings.some(
    s => s.operator === 'DISCOUNT' && Boolean(s.subOperator)
  )

// Whether picking this operator for the current product would leave at
// least one valid stored value for the user to enter. The "current row's
// own pick" escape is handled by the caller. Branches per top-level
// operator key via `ts-pattern` so adding a new operator only adds a new
// branch.
const isOperatorViable = (params: {
  key: ProductOperator
  productId: string | undefined
  sameProductSiblings: SameProductSibling[]
}): boolean => {
  const { key, productId, sameProductSiblings } = params
  if (!productId) return true

  return match(key)
    .with('HAS_DISCOUNT', () => {
      // Once per product. A DISCOUNT sibling doesn't block HAS_DISCOUNT —
      // the yes/no field is constrained to "yes" instead (see
      // `hasCommittedDiscountSibling`).
      return !sameProductSiblings.some(s => s.operator === 'HAS_DISCOUNT')
    })
    .with('DISCOUNT', () => {
      // A sibling that declared "this product has no discount" rules out
      // comparing discount values on it.
      const hasNoDiscountSibling = sameProductSiblings.some(
        s => s.operator === 'HAS_DISCOUNT' && s.hasDiscount === false
      )
      if (hasNoDiscountSibling) return false

      // Otherwise viable as long as the sub-operator range still has room:
      // EQUALS blocks the range, a complete range blocks EQUALS, and each
      // sub-operator can only appear once per product.
      const usedSubOperators = sameProductSiblings
        .filter(s => s.operator === 'DISCOUNT')
        .map(s => s.subOperator)
        .filter(isDiscountSubOperator)
      if (usedSubOperators.includes('EQUALS')) return false
      if (
        usedSubOperators.includes('LESS_THAN') &&
        usedSubOperators.includes('MORE_THAN')
      ) {
        return false
      }
      return true
    })
    .with('PRICING_MODEL', () => {
      // Once per product. Two pricing-model rows on the same product would
      // contradict each other (a product's prices can't all be both LINEAR
      // and GRADUATED at once); express alternatives in a separate group.
      return !sameProductSiblings.some(s => s.operator === 'PRICING_MODEL')
    })
    .with('BILLING_FREQUENCY', () =>
      billingFrequencyRowAvailable(sameProductSiblings)
    )
    .with('BILLING_TYPE', () => {
      // Once per product. "Is" being the only comparison on offer, capping the
      // rows is what stops a product being asked for both types at once —
      // which is the exhaustion rule as written. Billing frequency needs more
      // than this because its "is not" rows stack.
      return !sameProductSiblings.some(s => s.operator === 'BILLING_TYPE')
    })
    .exhaustive()
}

type PrimaryOperatorOnChange = (selected: string | number) => void

/**
 * Consolidates the primary-operator state shared by every PRODUCT editor: the
 * sibling lookup, the available operator options (taking sibling rows on
 * the same product into account), the disabled flag for the dropdown, and
 * the onChange handler that switches operator and seeds HAS_DISCOUNT with
 * "yes" by default.
 */
export const usePrimaryOperatorState = (params: {
  operator: string
  productId: string | undefined
  operators: Record<string, ConditionalOperator>
  siblingConditions: Array<Condition>
  onChange: (next: { operator?: string; value?: ProductConditionValue }) => void
}): {
  operatorOptions: Array<{ label: string; value: ProductOperator }>
  primaryOperatorDisabled: boolean
  onPrimaryOperatorChange: PrimaryOperatorOnChange
  sameProductSiblings: SameProductSibling[]
} => {
  const { operator, productId, operators, siblingConditions, onChange } = params

  const sameProductSiblings = useSameProductSiblings(
    productId,
    siblingConditions
  )

  const operatorOptions = PRODUCT_OPERATOR_KEYS.filter(
    key =>
      key === operator ||
      isOperatorViable({ key, productId, sameProductSiblings })
  ).map(key => ({
    label: operators[key]?.label ?? key,
    value: key
  }))

  const primaryOperatorDisabled = operatorOptions.length === 0

  // When the picked product changes, the previously chosen operator can
  // become non-viable for the new product (e.g. the new product already
  // has a sibling row using HAS_DISCOUNT). The dropdown keeps the current
  // pick visible via the "current row's own pick" escape, so without this
  // reset the row would silently sit in an invalid state. Reset to blank
  // so the user has to pick again from the now-filtered options.
  const currentOperatorIsKnown = PRODUCT_OPERATOR_KEYS.includes(
    operator as ProductOperator
  )
  const currentOperatorViable =
    !currentOperatorIsKnown ||
    isOperatorViable({
      key: operator as ProductOperator,
      productId,
      sameProductSiblings
    })
  useEffect(() => {
    if (!productId) return
    if (currentOperatorViable) return
    onChange({ operator: '', value: { productId } })
  }, [productId, currentOperatorViable, onChange])

  const onPrimaryOperatorChange: PrimaryOperatorOnChange = selected => {
    const nextOperator = String(selected)
    const baseValue: ProductConditionValue = { productId: productId ?? '' }
    const nextValue = match(nextOperator)
      .with('HAS_DISCOUNT', () => ({ ...baseValue, hasDiscount: true }))
      .with('PRICING_MODEL', 'BILLING_TYPE', () => ({
        ...baseValue,
        operator: 'IS' as IsSubOperator
      }))
      .with('BILLING_FREQUENCY', () => ({
        ...baseValue,
        operator: defaultBillingFrequencySubOperator(sameProductSiblings)
      }))
      .otherwise(() => baseValue)
    onChange({ operator: nextOperator, value: nextValue })
  }

  return {
    operatorOptions,
    primaryOperatorDisabled,
    onPrimaryOperatorChange,
    sameProductSiblings
  }
}
